import { invalidateChildrenAndSearch, useProjectsCacheKeys } from 'stores/rq/projects';
import { useGetUserPermissions } from 'stores/rq/user-permissions';
import { useQueryClient } from '@tanstack/react-query';
import useStores from 'hooks/use-stores.hooks';
import { useCallback } from 'react';
import useSubscription from './use-subscription';

export const usePermissionChangesSubscriber = (): void => {
  const { userStore, authStore } = useStores();
  const queryClient = useQueryClient();
  const { all, tree, childrenPrefix, searchPrefix } = useProjectsCacheKeys();
  const { refetch: refetchPermissions } = useGetUserPermissions();

  // Invalidating rather than holding these queries keeps the subscriber from enabling the org-wide project
  // fetch on every authenticated page.
  const refetchProjects = useCallback(async (): Promise<void> => {
    // need to refresh the token to get the new permissions
    await authStore.silentRenewSession();
    await refetchPermissions();
    await queryClient.invalidateQueries({ queryKey: all });
    await queryClient.invalidateQueries({ queryKey: tree });
    invalidateChildrenAndSearch(queryClient, childrenPrefix, searchPrefix);
  }, [authStore, refetchPermissions, queryClient, all, tree, childrenPrefix, searchPrefix]);

  useSubscription({
    stream: 'permissionsChanged',
    key: userStore.profile?.sub,
    onEvent: refetchProjects,
    skipInitEvent: true
  });
};
